/**
 * Privacy, terms and about, on one route with three anchors.
 *
 * WHY THESE EXIST AT ALL
 * ----------------------
 * They are not decoration and not optional. Google AdSense refuses sites
 * without a privacy policy, and the policy has to name what is collected and by
 * whom — including the ad network's own cookies. A reviewer looking for it and
 * not finding it is the most common reason a small site is rejected.
 *
 * They also carry the disclosures this project owes its readers regardless of
 * advertising: where the data comes from, how current it is, and what it does
 * not tell you.
 *
 * Written as plain statements of what actually happens, not boilerplate. Every
 * claim here is one the code can be checked against.
 */

import { Link } from 'react-router-dom';

const UPDATED = '25 August 2026';

export function LegalPage(): React.JSX.Element {
  return (
    <div className="legal">
      <Link className="btn btn-sm" to="/">
        ← Back to rankings
      </Link>

      <nav className="legal-nav" aria-label="Sections">
        <a href="#about">About</a>
        <a href="#data">Data &amp; sources</a>
        <a href="#privacy">Privacy</a>
        <a href="#terms">Terms</a>
        <a href="#contact">Contact</a>
      </nav>

      <section id="about" className="panel">
        <h1>About KcalTrack</h1>
        <p>
          KcalTrack ranks Singapore supermarket products by nutritional value for money —
          protein per dollar, cheapest per 100 kcal, highest protein, and similar measures.
          It exists to answer one question: <em>given what you care about, which product
          gives you the most of it?</em>
        </p>
        <p>
          It is an independent project. It is not affiliated with, endorsed by, or operated
          by NTUC FairPrice or any retailer named on the site.
        </p>
      </section>

      <section id="data" className="panel">
        <h2>Where the data comes from</h2>
        <p>
          Prices and product details come from FairPrice’s own published product feeds.
          Nutrition comes from four sources, and every figure on the site is labelled with
          which one it used:
        </p>
        <ul>
          <li>
            <strong>Product label</strong> — the nutrition panel FairPrice publishes for
            that exact product.
          </li>
          <li>
            <strong>Barcode match</strong> — an Open Food Facts entry whose barcode equals
            the product’s.
          </li>
          <li>
            <strong>Matched product</strong> — brand, product name and pack size all agreed
            independently. Not matched by barcode.
          </li>
          <li>
            <strong>Generic estimate</strong> — a composition table for that <em>type</em>{' '}
            of food, used only for whole foods that carry no manufacturer’s panel. Always
            marked differently from the other three.
          </li>
        </ul>
        <h3>What it does not tell you</h3>
        <ul>
          <li>
            Nutrition coverage is partial. The figure shown on the rankings page is the real
            proportion of the catalogue with data, and it is not close to 100%.
          </li>
          <li>
            “Protein per dollar” measures how cheaply protein can be bought. It says nothing
            about protein <em>quality</em> — amino-acid profile and digestibility differ
            between sources, and we hold no data for either.
          </li>
          <li>
            Prices change. Each product page states when its price was last observed. Always
            check the retailer before buying.
          </li>
        </ul>
      </section>

      <section id="privacy" className="panel">
        <h2>Privacy</h2>
        <p className="legal-updated">Last updated {UPDATED}</p>

        <h3>What this site stores about you</h3>
        <p>
          There are no accounts and no sign-in. Your criteria, comparison list and tracked
          products are kept in your browser’s local storage, on your device. They are not
          sent to us and we cannot read them.
        </p>
        <p>
          A random identifier is generated in your browser so that price-tracking works
          across visits. It is not linked to a name, an email address or any other
          identifying detail.
        </p>

        <h3>Advertising</h3>
        <p>
          This site shows advertising, including through Google AdSense. Third-party vendors,
          including Google, use cookies to serve ads based on your prior visits to this and
          other websites.
        </p>
        <p>
          Google’s use of advertising cookies enables it and its partners to serve ads based
          on your visits to this site and other sites. You can opt out of personalised
          advertising by visiting{' '}
          <a href="https://www.google.com/settings/ads" rel="noopener noreferrer" target="_blank">
            Google Ads Settings
          </a>
          , or opt out of third-party vendor cookies at{' '}
          <a href="https://www.aboutads.info" rel="noopener noreferrer" target="_blank">
            aboutads.info
          </a>
          .
        </p>

        <h3>Affiliate links</h3>
        <p>
          Some products shown are affiliate listings, and we may earn a commission if you buy
          through them. These are always marked as advertising. An affiliate product is
          ranked by exactly the same formula as every other product, using its delivered
          price including shipping — a position cannot be bought.
        </p>

        <h3>Analytics</h3>
        <p>
          We record anonymous, aggregate counts of which rankings are viewed, to know what is
          worth improving. No personal data is collected or sold.
        </p>
      </section>

      <section id="terms" className="panel">
        <h2>Terms of use</h2>
        <p className="legal-updated">Last updated {UPDATED}</p>
        <p>
          KcalTrack is provided as-is, for information only. It is not nutritional, dietary
          or medical advice. Speak to a qualified professional before making decisions about
          your diet, particularly if you have a medical condition or allergy.
        </p>
        <p>
          We work hard to keep prices and nutrition accurate and to say plainly where each
          figure came from, but we cannot guarantee that any figure is current or correct.
          Allergen information in particular must be checked on the physical packaging before
          you consume anything — do not rely on this site for it.
        </p>
        <p>
          Product names, brands and images belong to their respective owners and are used to
          identify the products described.
        </p>
      </section>

      <section id="contact" className="panel">
        <h2>Contact</h2>
        <p>
          Corrections are welcome, particularly if you spot a nutrition figure that does not
          match the packaging. That is the single most useful thing anyone can report.
        </p>
        <p>
          Email: <a href="mailto:xianxiansmoney@gmail.com">xianxiansmoney@gmail.com</a>
        </p>
      </section>
    </div>
  );
}
